/**
 * Inquiry Component
 * 문의 폼 처리
 */
export class Inquiry {
    constructor() {
        this.form = document.getElementById("inquiry-form");
        if (!this.form) return;

        this.init();
    }

    init() {
        // 탭 전환
        this.initTabs();

        // 카테고리 버튼 클릭 이벤트
        this.initCategoryButtons();

        // 파일 업로드 처리
        this.initFileUpload();

        // 견적문의 페이지는 인라인 스크립트에서 제출 처리 (중복 AJAX 방지)
        if (this.form.dataset.submitHandler === "inline") {
            return;
        }

        // 폼 제출 이벤트
        this.form.addEventListener("submit", (e) => {
            e.preventDefault();
            this.handleSubmit();
        });
    }

    initTabs() {
        const tabButtons = document.querySelectorAll(".no-inquiry__tab-btn");
        const tabContents = document.querySelectorAll(
            ".no-inquiry__tab-content",
        );

        tabButtons.forEach((btn) => {
            btn.addEventListener("click", () => {
                const targetTab = btn.dataset.tab;

                // 모든 탭 버튼 비활성화
                tabButtons.forEach((b) => b.classList.remove("--active"));
                // 클릭한 버튼 활성화
                btn.classList.add("--active");

                // 모든 콘텐츠 숨김
                tabContents.forEach((c) => c.classList.remove("--active"));
                // 해당 콘텐츠 표시
                const targetContent = document.querySelector(
                    `[data-content="${targetTab}"]`,
                );
                if (targetContent) {
                    targetContent.classList.add("--active");
                }
            });
        });
    }

    initCategoryButtons() {
        const categoryButtons = this.form.querySelectorAll(
            ".no-inquiry-form__category-btn",
        );
        const categoryInput = document.getElementById("inquiry_category");

        categoryButtons.forEach((btn) => {
            btn.addEventListener("click", () => {
                // 모든 버튼에서 active 제거
                categoryButtons.forEach((b) => b.classList.remove("is-active"));
                // 클릭한 버튼에 active 추가
                btn.classList.add("is-active");
                // hidden input에 값 설정
                if (categoryInput) {
                    categoryInput.value = btn.dataset.category || "";
                }
            });
        });
    }

    initFileUpload() {
        const fileInput = document.getElementById("attachment");
        const fileName = this.form.querySelector(".no-inquiry-form__file-name");

        if (fileInput && fileName) {
            fileInput.addEventListener("change", (e) => {
                const file = e.target.files[0];
                if (file) {
                    fileName.textContent = file.name;
                } else {
                    fileName.textContent = "";
                }
            });
        }
    }

    async handleSubmit() {
        // 유효성 검사
        if (!this.validateForm()) {
            return;
        }

        const submitBtn = this.form.querySelector('button[type="submit"]');
        const btnText = submitBtn.querySelector(".no-btn__text");
        const btnLoader = submitBtn.querySelector(".no-btn__loader");
        const submittingText = submitBtn.dataset.submittingText || "제출 중...";
        const originalText = btnText.textContent;

        // 제출 중 상태로 변경
        submitBtn.disabled = true;
        btnText.textContent = submittingText;
        if (btnLoader) {
            btnLoader.style.display = "inline-flex";
        }

        try {
            // 폼 데이터 수집
            const formData = new FormData(this.form);

            // CSRF 토큰 추가
            const csrfToken =
                document.querySelector('meta[name="csrf-token"]')?.content ||
                document.querySelector('input[name="_csrf_token"]')?.value;
            if (csrfToken) {
                formData.append("_csrf_token", csrfToken);
            }

            // 제출
            const response = await fetch(this.form.action, {
                method: "POST",
                body: formData,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            const result = await response.json();

            if (result.success) {
                // 성공 메시지 표시
                this.showMessage(result.message, "success");
                // 폼 리셋
                this.resetForm();
            } else {
                // 에러 메시지 표시
                this.showMessage(
                    result.message || "문의 접수 중 오류가 발생했습니다.",
                    "error",
                );
            }
        } catch (error) {
            console.error("Form submission error:", error);
            this.showMessage(
                "문의 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
                "error",
            );
        } finally {
            // 제출 버튼 원래 상태로 복원
            submitBtn.disabled = false;
            btnText.textContent = originalText;
            if (btnLoader) {
                btnLoader.style.display = "none";
            }
        }
    }

    validateForm() {
        // 카테고리 선택 확인
        const categoryInput = document.getElementById("inquiry_category");
        if (!categoryInput || !categoryInput.value) {
            this.showMessage("카테고리를 선택해주세요.", "error");
            const categoryButtons = this.form.querySelectorAll(
                ".no-inquiry-form__category-btn",
            );
            categoryButtons[0]?.focus();
            return false;
        }

        // 필수 필드 확인
        const requiredFields = [
            { name: "subject", label: "제목" },
            { name: "content", label: "내용" },
            { name: "name", label: "이름" },
            { name: "email", label: "이메일" },
        ];

        for (const field of requiredFields) {
            const input = this.form.querySelector(`[name="${field.name}"]`);
            if (!input || !input.value.trim()) {
                this.showMessage(`${field.label}을(를) 입력해주세요.`, "error");
                input?.focus();
                return false;
            }
        }

        // 이메일 형식 확인
        const email = this.form.querySelector('[name="email"]');
        if (email && email.value) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(email.value)) {
                this.showMessage("올바른 이메일 주소를 입력해주세요.", "error");
                email.focus();
                return false;
            }
        }

        // 개인정보 동의 확인
        const privacyAgree = document.getElementById("privacy_agree");
        if (!privacyAgree || !privacyAgree.checked) {
            this.showMessage("개인정보 수집 및 이용에 동의해주세요.", "error");
            privacyAgree?.focus();
            return false;
        }

        return true;
    }

    showMessage(message, type = "info") {
        // 기존 메시지 제거
        const existingMessage = document.querySelector(
            ".no-inquiry-form__message",
        );
        if (existingMessage) {
            existingMessage.remove();
        }

        // 새 메시지 생성
        const messageEl = document.createElement("div");
        messageEl.className = `no-inquiry-form__message --${type}`;
        messageEl.textContent = message;

        // 폼 앞에 삽입
        this.form.insertBefore(messageEl, this.form.firstChild);

        // 5초 후 자동 제거
        setTimeout(() => {
            messageEl.remove();
        }, 5000);
    }

    resetForm() {
        this.form.reset();

        // 카테고리 버튼 리셋
        const categoryButtons = this.form.querySelectorAll(
            ".no-inquiry-form__category-btn",
        );
        categoryButtons.forEach((btn) => btn.classList.remove("is-active"));

        // 파일명 리셋
        const fileName = this.form.querySelector(".no-inquiry-form__file-name");
        if (fileName) {
            fileName.textContent = "";
        }
    }
}
